import { useMutation } from "@tanstack/react-query";

import { fetchDataBlob } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { logger } from "@/core/utils/func/logger";
import type { ReportingService } from "@/features/reporting/api/endpoints";
import { reportingEndpoints } from "@/features/reporting/api/endpoints";
import type { ReportingSeriesParams } from "@/features/reporting/api/dto";
import { compactQuery } from "@/features/reporting/api/parse";

export interface ReportingExportParams extends ReportingSeriesParams {
  service: ReportingService;
  filename?: string;
}

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Export CSV d'une série (`GET .../Reporting/export.csv`).
 * Séparateur `;` + BOM UTF-8 côté serveur (Excel FR).
 */
export const useReportingExport = () =>
  useMutation({
    mutationKey: ["reporting", "export"],
    mutationFn: async ({
      service,
      from,
      to,
      metricKey,
      currency,
      filename,
    }: ReportingExportParams) => {
      const blob = await fetchDataBlob({
        url: reportingEndpoints[service].exportCsv,
        queryParams: compactQuery({
          from,
          to,
          metricKey,
          currency,
        }),
        acceptHeader: "text/csv",
      });
      if (blob.type.includes("json")) {
        const text = await blob.text();
        throw new Error(text || "CSV export failed");
      }
      const stamp = [from, to].filter(Boolean).join("_") || "export";
      downloadBlob(blob, filename ?? `${service}-reporting_${stamp}.csv`);
      return blob;
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useReportingExport failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "reporting" }
      );
    },
  });
